/**
 * Hook for managing the collection linked to an idea.
 *
 * Fetches the existing collection (if any) and provides a method
 * to lazily create one when first needed (e.g. when uploading media).
 */

import { useState, useEffect, useCallback } from "react";
import * as collectionsService from "@/services/collectionsService";

interface UseIdeaCollectionResult {
  collectionId: string | null;
  isCreating: boolean;
  /** Returns the collection ID, creating one if it doesn't exist yet. */
  ensureCollection: () => Promise<string | null>;
  /** Re-fetch the collection for this idea (e.g. after linking) */
  refresh: () => void;
}

export function useIdeaCollection(
  ideaId: string | null | undefined,
  clientId: string
): UseIdeaCollectionResult {
  const [collectionId, setCollectionId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!ideaId) {
      setCollectionId(null);
      return;
    }
    let cancelled = false;
    collectionsService
      .getCollectionByIdea(ideaId)
      .then((collection) => {
        if (!cancelled) setCollectionId(collection?.id ?? null);
      })
      .catch(() => {
        if (!cancelled) setCollectionId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [ideaId, refreshKey]);

  const refresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  const ensureCollection = useCallback(async (): Promise<string | null> => {
    if (collectionId) return collectionId;
    if (!ideaId || !clientId) return null;
    setIsCreating(true);
    try {
      const collection = await collectionsService.createCollection(clientId, {
        ideaId,
      });
      setCollectionId(collection.id);
      return collection.id;
    } catch (err) {
      console.error("Failed to create collection for idea:", err);
      return null;
    } finally {
      setIsCreating(false);
    }
  }, [collectionId, ideaId, clientId]);

  return { collectionId, isCreating, ensureCollection, refresh };
}
